import { ReactElement, useMemo, useCallback, memo } from 'react';
import { ColumnDescription } from 'react-bootstrap-table-next';
import classNames from 'classnames';

import { VehicleSearchItem } from '~services/vehicle/types';
import {
  VehicleInfo,
  SkillsListInfo,
  CapabilitiesInfo,
  IsAvailableChip,
  DataTable,
} from '~components/index';

import { TABLE_KEY_FIELDS } from '../../../../../utils/constants';
import { getDisabledVehicle } from '../../../../../utils/helpers';

import { useAssistantGenerateRoutesDialogByItemsContext } from '../../../../Context';

import { AssistantGenerateRoutesDialogByItemsContentFormTableContainer } from '../styles';

import { AssistantGenerateRoutesDialogByItemsContentFormVehiclesTableProps } from './types';

const AssistantGenerateRoutesDialogByItemsContentFormVehiclesTable = ({
  loading,
  onChange,
  pagination,
  totalSize,
}: AssistantGenerateRoutesDialogByItemsContentFormVehiclesTableProps): ReactElement => {
  const {
    data: {
      vehicles: { value: data },
    },
    form: {
      selection: {
        vehicles: { selectRowsProps },
      },
    },
  } = useAssistantGenerateRoutesDialogByItemsContext();

  const columns = useMemo<ColumnDescription<VehicleSearchItem>[]>(
    () => [
      {
        dataField: 'name',
        text: 'Vehículo',
        formatter: (cell, row) => (
          <VehicleInfo
            vehicle={cell}
            vehicleCode={row.referenceCode ?? ''}
            color={row.color}
            fontWeight="bold"
            useCaption
          />
        ),
      },
      {
        dataField: 'skillsList',
        text: 'Cargas esp.',
        formatter: (cell) => <SkillsListInfo skills={cell} />,
        headerStyle: { width: 200 },
        headerAlign: 'center',
        align: 'center',
      },
      {
        dataField: 'capacity1',
        text: 'Capacidades',
        formatter: (_cell, row) => (
          <CapabilitiesInfo
            capacity1={row.capacity1}
            capacity2={row.capacity2}
            maximumVisits={row.maximumVisits}
            timeWindow={{ timeFrom: row.timeFrom, timeTo: row.timeTo }}
            fontSize={12}
            fontWeight="bold"
          />
        ),
        headerStyle: { width: 200 },
      },
      {
        dataField: 'isAvailable',
        text: 'Estado',
        formatter: (_cell, row) => (
          <IsAvailableChip
            isAvailable={row.isAvailable}
            isOpenForWork={row.isOpenForWork}
            size="medium"
            variant="filled"
          />
        ),
        headerStyle: { width: 200 },
        headerAlign: 'center',
        align: 'center',
      },
    ],
    [],
  );

  const getDisabledRowClases = useCallback(
    (row: VehicleSearchItem): string =>
      classNames({ 'table-row-disabled': getDisabledVehicle(row) }),
    [],
  );

  return (
    <AssistantGenerateRoutesDialogByItemsContentFormTableContainer>
      <DataTable
        loading={loading}
        data={data}
        columns={columns}
        keyField={TABLE_KEY_FIELDS.VEHICLE}
        pagination={{
          options: {
            ...pagination,
            totalSize: totalSize,
            showTotal: false,
            hideSizePerPage: true,
          },
        }}
        remote={{
          pagination: true,
        }}
        onTableChange={(type, newState) =>
          onChange(type, { pagination: newState })
        }
        selectRow={selectRowsProps}
        rowClasses={getDisabledRowClases}
        condensed
      />
    </AssistantGenerateRoutesDialogByItemsContentFormTableContainer>
  );
};

export default memo(
  AssistantGenerateRoutesDialogByItemsContentFormVehiclesTable,
);
